import React, { useState, useCallback, useEffect } from 'react';

import { Stack, Group, Text, useMantineTheme, createStyles } from '@mantine/core';
import { Eventcalendar, MbscCalendarEvent } from '@mobiscroll/react';
import dayjs from 'dayjs';
import useDebounce from 'hooks/useDebounce';
import useWorkoutsCalendar from 'hooks/workout/useWorkoutsCalendar';
import type { KiloClassType, WorkoutCalendar, WorkoutCalendarKiloClassType } from 'types';

import type { FilterTerms, ProgramCalendarView, ProgramCalendarWorkoutAssigned } from './types';

const FALLBACK_CLASS_TYPE_COLOR = '#6b7280';

function toKiloClassType(row: WorkoutCalendarKiloClassType): KiloClassType {
  const color = row.color?.trim() || FALLBACK_CLASS_TYPE_COLOR;
  return {
    kiloClassTypeId: row.kiloClassTypeId,
    name: row.name,
    color,
    gym: '',
    description: null,
    duration: 0,
    capacity: 0,
    waitlistLimit: null,
    location: null,
    deletedAt: null,
    createdAt: '',
    updatedAt: '',
    reservationAndCancellationSettings: {},
  } as KiloClassType;
}

export interface CalendarTileData {
  id: string;
  programId: string;
  start: Date;
  programName: string;
  totalPrograms: number;
  workoutNotes: string | null;
  kiloClassType: KiloClassType;
  createdAt: string;
  updatedAt: string;
  gym: string;
  programDate: string;
  isProgrammed: boolean;
  programmingKiloClassTypeId: string;
  programmingClassTypeColor: string;
  programmingDisplayName: string;
  programmingAssignedWorkout: ProgramCalendarWorkoutAssigned | null;
}

interface IProgramCalendarMonthViewProps {
  programCalendarNavigationButtons: () => JSX.Element;
  searchFilters: FilterTerms;
  setView: React.Dispatch<React.SetStateAction<ProgramCalendarView>>;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  calendarMaxDate?: Date;
}

const useStyles = createStyles(theme => ({
  calendar: {
    fontFamily: `${theme.fontFamily} !important`,
    borderRadius: theme.radius.md,
  },
}));

/**
 * Programming month view (workout tracker on): programs/calendar + workouts/calendar merged; opens workout programming drawer.
 */
const ProgramCalendarMonthView = ({
  programCalendarNavigationButtons,
  searchFilters,
  setView,
  setDate,
  calendarMaxDate,
}: IProgramCalendarMonthViewProps): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const [start, setStart] = useState<string>();
  const [end, setEnd] = useState<string>();
  const debouncedStart = useDebounce(start, 200);
  const debouncedEnd = useDebounce(end, 200);
  const [calendarEvents, setCalendarEvents] = useState<MbscCalendarEvent[]>([]);

  const {
    data: workoutCalData,
    isSuccess: workoutCalSuccess,
    isError: workoutCalError,
  } = useWorkoutsCalendar({
    startDate: debouncedStart,
    endDate: debouncedEnd,
  });

  const onPageLoading = (event: {
    firstDay: string | number | Date | dayjs.Dayjs | null | undefined;
    lastDay: string | number | Date | dayjs.Dayjs | null | undefined;
  }): void => {
    const s = dayjs(event.firstDay).format('YYYY-MM-DD');
    const e = dayjs(event.lastDay).format('YYYY-MM-DD');
    setStart(s);
    setEnd(e);
  };

  const handleCellClick = useCallback(
    (data: any): void => {
      setDate(dayjs(data.date).format('YYYY-MM-DD'));
      setView('agenda');
    },
    [setDate, setView],
  );

  const renderLabel = useCallback(
    (data: any): JSX.Element => {
      const { totalPrograms, kiloClassType, programName, isProgrammed } = data.original;
      return (
        <Stack p={0} spacing='xs' data-cy='programMonthItem'>
          <Group
            sx={{
              gap: 0,
              backgroundColor: isProgrammed ? kiloClassType.color : 'grey',
              borderRadius: `calc(${theme.spacing.lg} / 5)`,
            }}
            p={`calc(${theme.spacing.xs} /4 )`}
          >
            <Text size='md' fw='bold' pl='xs'>
              {programName}
            </Text>
          </Group>
          <Group sx={{ gap: `calc(${theme.spacing.xs} / 1)`, p: 0 }}>
            {totalPrograms > 1 && (
              <Text size='md' pl='xs'>
                {totalPrograms - 1} more
              </Text>
            )}
          </Group>
        </Stack>
      );
    },
    [theme],
  );

  useEffect(() => {
    if (!workoutCalSuccess || !workoutCalData) return;

    const workoutDays = workoutCalData.data as WorkoutCalendar[];
    const tiles: CalendarTileData[] = [];

    for (const day of workoutDays) {
      const dateStr = day.date;
      const allRows = day.kiloClassTypes ?? [];
      const rows =
        searchFilters.kiloClassTypes.length > 0
          ? allRows.filter(k => searchFilters.kiloClassTypes.includes(k.kiloClassTypeId))
          : allRows;

      if (rows.length === 0) continue;

      const rowForTile = rows.find(r => r.workout != null) ?? rows[0];
      const total = rows.length;
      const anyWorkout = rows.some(r => r.workout != null);
      const programmingAssignedWorkout: ProgramCalendarWorkoutAssigned | null =
        rowForTile.workout != null
          ? { workoutId: rowForTile.workout.workoutId, name: rowForTile.workout.name }
          : null;
      const kiloClassType = toKiloClassType(rowForTile);

      tiles.push({
        start: new Date(`${dateStr}T00:00`),
        totalPrograms: total,
        id: `wc-month-${dateStr}-${rowForTile.kiloClassTypeId}`,
        programId: '',
        programDate: dateStr,
        workoutNotes: null,
        kiloClassType,
        programName: rowForTile.name,
        createdAt: '',
        updatedAt: '',
        gym: '',
        isProgrammed: anyWorkout,
        programmingKiloClassTypeId: rowForTile.kiloClassTypeId,
        programmingClassTypeColor: kiloClassType.color,
        programmingDisplayName: rowForTile.name,
        programmingAssignedWorkout,
      });
    }

    let maxPrograms = 1;
    tiles.forEach(t => {
      if (t.totalPrograms > maxPrograms) maxPrograms = t.totalPrograms;
    });

    setCalendarEvents(
      tiles.map(
        (event: CalendarTileData): MbscCalendarEvent => ({
          ...event,
          backgroundColor: `rgb(0, 158, 227, ${event.totalPrograms / maxPrograms})`,
        }),
      ),
    );
  }, [workoutCalSuccess, workoutCalData, searchFilters.kiloClassTypes]);

  if (workoutCalError) {
    return <>Something went wrong</>;
  }

  return (
    <Eventcalendar
      renderHeader={programCalendarNavigationButtons}
      theme='ios'
      themeVariant='light'
      className={classes.calendar}
      data={calendarEvents}
      renderLabel={renderLabel}
      onPageLoading={onPageLoading}
      onCellClick={handleCellClick}
      max={calendarMaxDate}
      view={{ calendar: { labels: true } }}
    />
  );
};

export default ProgramCalendarMonthView;
